import { UserOutlined } from "@ant-design/icons";
import { Avatar } from "antd";
import React from "react";
import { DEFAULT_PROFILE_AVATAR } from "src/utils";
import styled from "styled-components";

type AvatarSize = "small" | "default" | "large";

type UserProfile = {
    firstName?: string;
    lastName?: string;
    email?: string;
};

type UserAvatarProps = UserProfile & {
    photoUrl?: string;
    size: AvatarSize;
};

/** Keeps the monogram visually centred with room to breathe inside the circle. */
const MONOGRAM_FONT_SIZE: Record<AvatarSize, number> = {
    small: 10,
    default: 13,
    large: 16,
};

/**
 * The logged in user's profile arrives nested or flat depending on whether it
 * came from the login or the user info request.
 */
export const resolveUserProfile = (userInfoResponse: any, loginResponse: any): UserProfile =>
    userInfoResponse?.userProfile || loginResponse?.userProfile || userInfoResponse || {};

export const getInitials = ({ firstName, lastName, email }: UserProfile) => {
    const first = firstName?.trim() ?? "";
    const last = lastName?.trim() ?? "";

    if (first && last) {
        return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }

    if (first) {
        const parts = first.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
        }
        return first.charAt(0).toUpperCase();
    }

    if (last) return last.charAt(0).toUpperCase();

    const letter = email?.trim().charAt(0);
    return letter ? letter.toUpperCase() : "";
};

/**
 * Falls back from photo, to initials, to the email's first letter, to a glyph.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({ photoUrl, firstName, lastName, email, size }) => {
    const hasPhoto = !!photoUrl && !photoUrl.startsWith(DEFAULT_PROFILE_AVATAR);
    const initials = getInitials({ firstName, lastName, email });

    return (
        <Monogram
            size={size}
            src={hasPhoto ? photoUrl : undefined}
            icon={initials ? undefined : <UserOutlined />}
        >
            {initials || undefined}
        </Monogram>
    );
};

export default UserAvatar;

const Monogram = styled(Avatar)<{ size: AvatarSize }>`
    && {
        font-size: ${({ size }) => MONOGRAM_FONT_SIZE[size]}px;
    }

    flex-shrink: 0;
    background: #1d1d1f;
    color: ${({ theme }) => theme.color.white};
    font-family: ${({ theme }) => theme.fontFamily.body};
    font-weight: 500;
    letter-spacing: 0;
`;
